import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/Button";
import { EntityMultiSelect } from "@/components/EntityMultiSelect";
import { Modal, ModalSubtitle, ModalTitle } from "@/components/Modal";
import { Select } from "@/components/Select";
import { TextInput } from "@/components/TextInput";
import type { ToastVariant } from "@/components/Toast";
import { useApi } from "@/lib/api";
import { PAGE_SIZE, type PagedResult } from "@/lib/pagination";
import { RESOURCE_TYPE_LABELS } from "@/lib/resourceTypeLabels";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import type { AccessRule, OrgResource } from "./shared";

export function StaticRuleModal({
  rule,
  teamName,
  showToast,
  onClose,
  onSaved,
}: {
  rule: AccessRule | null;
  teamName: string;
  showToast: (message: string, variant: ToastVariant) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { accessToken } = useAuth();
  const api = useApi();
  const { orgId, teamId } = useParams();
  const [name, setName] = useState(rule?.name ?? "");
  const [resourceIds, setResourceIds] = useState<string[]>(
    rule?.resourceIds ?? [],
  );
  const [selections, setSelections] = useState<Record<string, OrgResource>>({});
  const [nameFilter, setNameFilter] = useState("");
  const debouncedNameFilter = useDebouncedValue(nameFilter, 300);
  const [typeFilter, setTypeFilter] = useState("");
  const [page, setPage] = useState(1);
  const [results, setResults] = useState<OrgResource[] | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!accessToken || !orgId) {
      return;
    }

    let cancelled = false;

    async function loadResources() {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
      });

      if (debouncedNameFilter) {
        params.set("name", debouncedNameFilter);
      }

      if (typeFilter) {
        params.set("type", typeFilter);
      }

      const data = await api.get<PagedResult<OrgResource>>(
        `/organizations/${orgId}/resources?${params.toString()}`,
      );

      if (cancelled) {
        return;
      }

      const items = data?.items ?? [];

      setResults(items);
      setTotalCount(data?.totalCount ?? 0);
      setSelections((current) => {
        const next = { ...current };

        for (const item of items) {
          next[item.id] = item;
        }

        return next;
      });
    }

    void loadResources();

    return () => {
      cancelled = true;
    };
  }, [accessToken, api, orgId, page, debouncedNameFilter, typeFilter]);

  function toggleResource(resource: OrgResource) {
    setSelections((current) => ({ ...current, [resource.id]: resource }));
    setResourceIds((current) =>
      current.includes(resource.id)
        ? current.filter((id) => id !== resource.id)
        : [...current, resource.id],
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!accessToken || !orgId || !teamId) {
      return;
    }

    setSubmitting(true);

    try {
      const body = { name, resourceIds };

      if (rule) {
        await api.send(
          `/organizations/${orgId}/teams/${teamId}/access-rules/${rule.id}`,
          "PATCH",
          body,
          "Couldn't update rule.",
        );
        showToast(`${name} updated.`, "success");
      } else {
        await api.send(
          `/organizations/${orgId}/teams/${teamId}/access-rules`,
          "POST",
          { ...body, kind: "Static" },
          "Couldn't create rule.",
        );
        showToast(`${name} added to ${teamName}.`, "success");
      }

      onSaved();
    } catch (err) {
      showToast(
        err instanceof Error ? err.message : "Couldn't save rule.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal onClose={onClose} scrollable={false}>
      <div className="mb-4 shrink-0">
        <ModalTitle>{rule ? "Edit static rule" : "Add static rule"}</ModalTitle>
        <ModalSubtitle className="mt-1">
          Grant access by selecting resources individually.
        </ModalSubtitle>
      </div>
      <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1">
          <div>
            <label
              htmlFor="staticRuleName"
              className="mb-1.5 block text-sm text-foreground"
            >
              Name
            </label>
            <TextInput
              type="text"
              id="staticRuleName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
              className="w-full"
            />
          </div>

          <EntityMultiSelect
            label="Resources"
            searchLabel="Search resources by name"
            searchPlaceholder="Search resources"
            selectedIds={resourceIds}
            options={results}
            emptyMessage={
              debouncedNameFilter
                ? `No resources match "${debouncedNameFilter}".`
                : "No resources match."
            }
            nameFilter={nameFilter}
            onNameFilterChange={(value) => {
              setNameFilter(value);
              setPage(1);
            }}
            page={page}
            totalPages={Math.ceil(totalCount / PAGE_SIZE)}
            onPageChange={setPage}
            onToggle={toggleResource}
            selections={selections}
            renderLabel={(resource) =>
              `${resource.name} (${RESOURCE_TYPE_LABELS[resource.type] ?? resource.type})`
            }
            filter={
              <Select
                aria-label="Filter by resource type"
                value={typeFilter}
                onChange={(e) => {
                  setTypeFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All types</option>
                {Object.entries(RESOURCE_TYPE_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </Select>
            }
          />
        </div>

        <div className="mt-4 flex shrink-0 justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={submitting || resourceIds.length === 0}
          >
            {submitting ? "Saving..." : rule ? "Save" : "Add rule"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
